import { type FC, useCallback, useEffect, useMemo } from 'react';
import type {
  CommissurePointsBoxProps as Props,
  ICustomCommissurePointsBoxController as BoxController
} from '@/components/boxes/objectBoxes/interfaces.ts';

import { observer } from 'mobx-react';
import { LabelBox } from '@/components/boxes/objectBoxes/CustomCommissurePointsBox/LabelBox/LabelBox.tsx';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { IconButton, Tooltip } from '@mui/material';
import { ButtonsContainer, Container } from '@/components/boxes/box-containers/boxContainers.ts';
import { useInjection } from 'inversify-react';
import { TYPES } from '@/engine/ioc';
import { useDisposableController } from '../../hooks/useDisposableController';
import { type BoxesIndicationStore } from '@/components/boxes/BoxesIndicationStore.ts';
import { TYPES as IndicationTypes } from '@/engine/app/modules/indications/iocTypes.ts';
import { type IReactionDisposer, reaction } from 'mobx';
import { EyeCheckboxRender } from '@/components/common/expandableTable/EyeCheckboxRender';
import type { ObjectBoxesController } from '../ObjectBoxes.controller';
import { PenCheckboxRender } from '@/components/common/expandableTable/PenCheckboxRender';

export const CustomCommissurePointsBox: FC<Props> = observer((props: Props) => {
  const mainController = useInjection<ObjectBoxesController>(TYPES.IObjectBoxesController);
  const boxesIndicationStore: BoxesIndicationStore = useInjection<BoxesIndicationStore>(
    IndicationTypes.BoxesIndicationStore
  );

  const ccpBoxController: BoxController = useDisposableController(
    useInjection<BoxController>(TYPES.CustomCommissurePointsBoxController),
    'commissure'
  );

  const handleAddClick = useCallback((): void => {
    boxesIndicationStore.setCurrentIndication(true);

    if (ccpBoxController.isIndicating) return;

    ccpBoxController.setIndicationState(!ccpBoxController.isIndicating);
  }, [boxesIndicationStore, ccpBoxController]);

  useEffect(() => {
    const disposer: IReactionDisposer = reaction(
      () => boxesIndicationStore.isOn,
      (): void => {
        if (ccpBoxController.isIndicating) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
          ccpBoxController.cancelIndicating();
        }
      }
    );

    return (): void => disposer();
  }, [boxesIndicationStore.isOn, ccpBoxController]);

  const allDescriptors = useMemo(() => props.objects.map((o) => o.descriptor), [props.objects]);

  return (
    <Container>
      <ButtonsContainer>
        <Tooltip title="Indicate new commissure point" placement="top">
          <span>
            <IconButton
              data-testid="indicateNewDistance"
              disabled={!ccpBoxController.isIndicating && props.objects.length > 4}
              aria-label="indicate"
              color={ccpBoxController.isIndicating ? 'primary' : 'secondary'}
              onClick={handleAddClick}
            >
              <AddCircleOutlineIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Toggle edit mode" placement="top">
          <span>
            <PenCheckboxRender
              onChange={(checked: boolean): void =>
                mainController.updateAllViewItemsBeingEdited(allDescriptors, checked)
              }
              isChecked={mainController.checkGroupViewItemsBeingEdited(allDescriptors)}
            />
          </span>
        </Tooltip>
        <Tooltip title="Toggle visibility" placement="top">
          <span>
            <EyeCheckboxRender
              onChange={(isVisible: boolean): void =>
                mainController.updateAllViewItemsVisibility(allDescriptors, isVisible)
              }
              isChecked={mainController.checkGroupViewItemsVisibility(allDescriptors)}
            />
          </span>
        </Tooltip>
      </ButtonsContainer>
      <LabelBox key={'dmbox' + props.index} objects={props.objects} boxController={ccpBoxController} />
    </Container>
  );
});
